import * as React from 'react';
import { NormalizedQuestionnairePageData } from './types';
import { PropsWithChildrenOnly } from '@ids/types/utility';
import {
    FetchedUniversalState,
    useFetchedUniversalState,
    useUniversalFetch,
    useUniversalFetchingHelpers,
} from '@ids/appshell';
import { QuestionnairePageResponse } from '@ids/types/now-api';

const QuestionnairePageContext = React.createContext<NormalizedQuestionnairePageData | undefined>(undefined);

QuestionnairePageContext.displayName = 'QuestionnairePageContext';

const normalizeQuestionnairePageData = (
    data: QuestionnairePageResponse | undefined
): NormalizedQuestionnairePageData | undefined => {
    if (!data || !data?.data?.length) {
        return undefined;
    }

    const questionnairePageData = data?.data[0];

    return {
        typeformId: questionnairePageData.typeformId,
        idsProductSimpleSku: questionnairePageData.idsProductSimpleSku,
        typeform: questionnairePageData.typeform,
        sparrowFormToken: questionnairePageData.sparrowFormToken,
    };
};

const QuestionnairePageProvider = ({ children }: PropsWithChildrenOnly) => {
    const { get } = useUniversalFetchingHelpers();

    const data = useFetchedUniversalState('fetchQuestionnairePage');

    useUniversalFetch(
        'fetchQuestionnairePage',
        async () => {
            if (!data) {
                return get<FetchedUniversalState['fetchQuestionnairePage']>({
                    path: '/web/v1/content/entries/newIdsQuestionnairePage',
                });
            }
            return undefined;
        },
        [data]
    );

    const value = React.useMemo(() => normalizeQuestionnairePageData(data), [data]);

    return <QuestionnairePageContext.Provider value={value}>{children}</QuestionnairePageContext.Provider>;
};

const useQuestionnairePageContext = () => {
    return React.useContext(QuestionnairePageContext);
};

export { QuestionnairePageProvider, useQuestionnairePageContext };
